/**
 * Per-record capabilities from the record-service envelope (OpenFGA).
 *
 * Type-level JWT roles (`GET-/PUT-/PATCH-Material`) answer "may I open this
 * schema at all?". Instance capabilities answer "may I edit THIS row, in this
 * context?". Absent capabilities are a no-op — the UI must not invent an ACL
 * from JWT or from view filters. See REQ-02 / CPES-14724 runtime security.
 *
 * BE contract (FE wrappers until Orval lists the fields):
 * - Collection `/query` and detail omit rows / fields the caller cannot see.
 * - Hidden detail PKs are 404 (not 403-with-body) so existence does not leak.
 * - Optional `metadata.capabilities` (or top-level `capabilities`) on a
 *   visible record: `{ canRead, canUpdate, canDelete, canPatch?: string[] }`.
 * - Relationship GET 403 means the map is withheld, not empty.
 */

import { PERMISSION_CHECKS_DISABLED } from "../hooks/useResourcePermissions";

export const RECORD_CAPABILITIES_KEY = "_capabilities";

export interface RecordCapabilities {
  canRead?: boolean;
  canUpdate?: boolean;
  canDelete?: boolean;
  /** Property names the caller may PATCH. Omitted = fall back to type-level. */
  canPatch?: string[];
}

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === "object" && !Array.isArray(value);

const readBool = (raw: Record<string, unknown>, key: string): boolean | undefined => {
  const value = raw[key];
  return typeof value === "boolean" ? value : undefined;
};

const readPatchList = (raw: unknown): string[] | undefined => {
  if (!Array.isArray(raw)) {
    return undefined;
  }
  const names = raw.filter(
    (entry): entry is string => typeof entry === "string" && entry.length > 0,
  );
  return names.length > 0 ? names : undefined;
};

const capabilitiesSource = (raw: Record<string, unknown>): Record<string, unknown> => {
  if (isPlainObject(raw.capabilities)) {
    return raw.capabilities;
  }
  if (isPlainObject(raw.permissions)) {
    return raw.permissions;
  }
  return raw;
};

/** Parse a capabilities object; omit when the shape is absent or empty. */
export const parseRecordCapabilities = (raw: unknown): RecordCapabilities | undefined => {
  if (!isPlainObject(raw)) {
    return undefined;
  }

  const source = capabilitiesSource(raw);

  const parsed: RecordCapabilities = {
    canRead: readBool(source, "canRead"),
    canUpdate: readBool(source, "canUpdate"),
    canDelete: readBool(source, "canDelete"),
    canPatch: readPatchList(source.canPatch),
  };

  if (
    parsed.canRead === undefined &&
    parsed.canUpdate === undefined &&
    parsed.canDelete === undefined &&
    parsed.canPatch === undefined
  ) {
    return undefined;
  }

  return parsed;
};

/** Capabilities already attached to a grid row / detail record. */
export const capabilitiesOf = (
  record: Record<string, unknown> | null | undefined,
): RecordCapabilities | undefined => {
  if (!record) {
    return undefined;
  }
  const attached = record[RECORD_CAPABILITIES_KEY];
  if (attached) {
    return parseRecordCapabilities(attached);
  }
  const meta = record._metadata;
  if (isPlainObject(meta)) {
    return parseRecordCapabilities(meta.capabilities ?? meta);
  }
  return parseRecordCapabilities(record.capabilities);
};

/** Copy capabilities onto a row without mutating the cached `data` object. */
export const attachRecordCapabilities = (
  data: Record<string, unknown>,
  capabilities: RecordCapabilities | undefined,
): Record<string, unknown> =>
  capabilities ? { ...data, [RECORD_CAPABILITIES_KEY]: capabilities } : data;

export const canUpdateWithInstance = (
  typeCanUpdate: boolean,
  capabilities: RecordCapabilities | undefined,
): boolean => {
  // TEMPORARY demo bypass — see PERMISSION_CHECKS_DISABLED; BE still enforces.
  // It skips the instance grant only: the type answer still stands, because a
  // read-only surface (the record peek) denies at that level.
  if (PERMISSION_CHECKS_DISABLED) {
    return typeCanUpdate;
  }
  return typeCanUpdate && capabilities?.canUpdate !== false;
};

export const canDeleteWithInstance = (
  typeCanDelete: boolean,
  capabilities: RecordCapabilities | undefined,
): boolean => {
  // TEMPORARY demo bypass — see PERMISSION_CHECKS_DISABLED; BE still enforces.
  if (PERMISSION_CHECKS_DISABLED) {
    return typeCanDelete;
  }
  return typeCanDelete && capabilities?.canDelete !== false;
};

export const canPatchWithInstance = (
  typeCanPatch: (property: string) => boolean,
  capabilities: RecordCapabilities | undefined,
  property: string,
): boolean => {
  // TEMPORARY demo bypass — see PERMISSION_CHECKS_DISABLED; BE still enforces.
  if (PERMISSION_CHECKS_DISABLED) {
    return typeCanPatch(property);
  }
  if (!typeCanPatch(property)) {
    return false;
  }
  if (!capabilities) {
    return true;
  }
  if (capabilities.canUpdate === false) {
    return false;
  }
  if (!capabilities.canPatch) {
    return true;
  }
  return capabilities.canPatch.includes(property);
};

export const withInstancePatchGate =
  (typeCanPatch: (property: string) => boolean, capabilities: RecordCapabilities | undefined) =>
  (property: string): boolean =>
    canPatchWithInstance(typeCanPatch, capabilities, property);

/** Relationships GET answered 403 — withheld, not an empty map. */
export const RELATIONSHIPS_RESTRICTED = Object.freeze({ _restricted: true as const });

export const isRelationshipsRestricted = (
  value: unknown,
): value is { readonly _restricted: true } =>
  value === RELATIONSHIPS_RESTRICTED || (isPlainObject(value) && value._restricted === true);
